"use client";

import { MeshGradient } from "@paper-design/shaders-react";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { useMotionPreference } from "@/hooks/use-motion-preference";

/**
 * Whole-page animated backdrop, powered by a single Paper Shaders MeshGradient.
 *
 * License note: @paper-design/shaders-react ships under PolyForm-Shield-1.0.0
 * (source-available; permits any use EXCEPT building a competing product).
 * moongate is a trading app, not a shaders/design tool, so our use is within
 * the license. Socket's default policy flags non-OSI licenses, so this one is
 * accepted there (see the @SocketSecurity ignore note on the PR).
 *
 * - Theme-aware: a deep cosmic purple in dark, an airy lavender in light.
 * - Scroll-aware: the gradient subtly zooms + rotates with `--scroll-progress`
 *   (published by the Locomotive provider) — pure CSS transform, so it's cheap.
 * - Capability-aware: real WebGL shader on capable devices; a cheap CSS nebula
 *   on mobile / low-power; `speed = 0` (no rAF) for reduced motion.
 * A theme-aware CSS starfield + vignette always render on top.
 */
const DARK_COLORS = ["#05010f", "#190a3a", "#4c1d95", "#7c3aed", "#c026d3"];
const LIGHT_COLORS = ["#faf5ff", "#ede9fe", "#ddd6fe", "#c4b5fd", "#a78bfa"];

export function ShaderBackground() {
  const { resolvedTheme } = useTheme();
  const { reducedMotion, isMobile } = useMotionPreference();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const isLight = resolvedTheme === "light";
  const colors = isLight ? LIGHT_COLORS : DARK_COLORS;
  // Shader runs on every device (incl. mobile) once mounted; the CSS nebula is
  // just the pre-hydration placeholder. Reduced-motion keeps it (speed = 0).
  const useShader = mounted;
  const speed = reducedMotion ? 0 : 0.25;

  // The scroll-linked zoom/rotation repaints a full-screen layer every scroll
  // frame — a big jank source on iOS — so it's desktop-only. The shader itself
  // still animates on mobile via its own `speed`.
  const scrollLinked = useShader && !reducedMotion && !isMobile;
  const shaderTransform = scrollLinked
    ? "scale(calc(1 + var(--scroll-progress, 0) * 0.35)) rotate(calc(var(--scroll-progress, 0) * 14deg))"
    : undefined;

  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <div
        className="absolute inset-0"
        style={{
          transform: shaderTransform,
          willChange: scrollLinked ? "transform" : undefined,
          opacity: isLight ? 0.85 : 1,
        }}
      >
        {useShader ? (
          <MeshGradient
            colors={colors}
            distortion={isLight ? 0.7 : 0.92}
            swirl={0.6}
            grainOverlay={isLight ? 0.02 : 0.06}
            speed={speed}
            scale={1.3}
            style={{ width: "100%", height: "100%" }}
          />
        ) : (
          // CSS nebula fallback (mobile / low-power / pre-mount). Pure radial
          // gradients — no `filter: blur()`, which is expensive to repaint on
          // phones — so the gradients' own falloff supplies the softness.
          <div className="absolute inset-0">
            <div
              className="absolute inset-0"
              style={{ background: "radial-gradient(60% 55% at 16% 10%, rgba(124,58,237,0.32), transparent 72%)" }}
            />
            <div
              className="absolute inset-0"
              style={{ background: "radial-gradient(55% 50% at 88% 22%, rgba(168,85,247,0.22), transparent 72%)" }}
            />
            <div
              className="absolute inset-0"
              style={{ background: "radial-gradient(65% 55% at 50% 104%, rgba(192,132,252,0.18), transparent 72%)" }}
            />
          </div>
        )}
      </div>

      {/* Soft vignette — only darkens the far edges so it never washes out the stars. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 50% 30%, transparent 55%, var(--color-background) 120%)",
        }}
      />

      {/* Starfield — white-ish in dark mode, drifts gently with scroll. */}
      <div
        className="absolute inset-0 hidden dark:block"
        style={{
          backgroundImage: `radial-gradient(1.4px 1.4px at 20px 30px, rgba(255,255,255,0.9), transparent),
            radial-gradient(1.2px 1.2px at 90px 110px, rgba(255,255,255,0.7), transparent),
            radial-gradient(1.6px 1.6px at 150px 60px, rgba(192,132,252,0.8), transparent),
            radial-gradient(1.2px 1.2px at 60px 150px, rgba(255,255,255,0.6), transparent)`,
          backgroundSize: "170px 170px",
          transform: scrollLinked
            ? "translateY(calc(var(--scroll-progress, 0) * -60px))"
            : undefined,
        }}
      />
      {/* Starfield — purple in light mode (white would be invisible on white). */}
      <div
        className="absolute inset-0 block dark:hidden"
        style={{
          backgroundImage: `radial-gradient(2px 2px at 20px 30px, rgba(109,40,217,0.85), transparent),
            radial-gradient(1.6px 1.6px at 90px 110px, rgba(147,51,234,0.7), transparent),
            radial-gradient(2px 2px at 150px 60px, rgba(124,58,237,0.8), transparent),
            radial-gradient(1.6px 1.6px at 60px 150px, rgba(168,85,247,0.65), transparent)`,
          backgroundSize: "130px 130px",
          transform: scrollLinked
            ? "translateY(calc(var(--scroll-progress, 0) * -60px))"
            : undefined,
        }}
      />
    </div>
  );
}
